import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Recycle, Building2, Search, Plus, Trash2, X, RefreshCw, Send,
    ChevronDown, Loader2, Layers,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchCostCenters,
    fetchCategories,
    fetchItemCodes,
    fetchGrid,
    fetchClients,
    fetchSubClients,
    fetchSaleMinDate,
    addItem,
    deleteItem,
    submitScrapSale,
    clearItemCodes,
    clearGrid,
    clearClients,
    clearSubClients,
    clearAddResult,
    clearSubmitResult,
    resetScrapSale,
    selectSSCCList,
    selectSSCategoryList,
    selectSSItemCodeList,
    selectSSGridItems,
    selectSSClientList,
    selectSSSubClientList,
    selectSSSaleMinDate,
    selectSSAddResult,
    selectSSSubmitResult,
    selectSSLoading,
    selectSSErrors,
} from '../../slices/purchaseSlice/scrapSaleSlice';

// ── Shared UI (mirrors DailyIssue / ItemsTransfer) ──────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const formatDateForSP = (d) => {
    if (!d) return '';
    if (d instanceof Date) {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    }
    return String(d);
};

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// SubClientCode comes back as "CODE , Branch Name" (see spGetSubClientsbyClient) —
// the real code the save SP expects is the part before the comma.
const subClientCodeOf = (subClientCodeLabel) => (subClientCodeLabel || '').split(',')[0].trim();

const ScrapSale = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userData?.employeeId || userData?.UID || 'system';
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';

    const ccList        = useSelector(selectSSCCList);
    const categoryList  = useSelector(selectSSCategoryList);
    const itemCodeList  = useSelector(selectSSItemCodeList);
    const gridItems      = useSelector(selectSSGridItems);
    const clientList     = useSelector(selectSSClientList);
    const subClientList  = useSelector(selectSSSubClientList);
    const saleMinDate     = useSelector(selectSSSaleMinDate);
    const addResult       = useSelector(selectSSAddResult);
    const submitResult    = useSelector(selectSSSubmitResult);
    const loading          = useSelector(selectSSLoading);
    const errors            = useSelector(selectSSErrors);

    const [selectedCC, setSelectedCC] = useState('');
    const [category, setCategory] = useState('');
    const [itemCode, setItemCode] = useState('');

    const [checkedRows, setCheckedRows] = useState({});
    const [basicEdits, setBasicEdits] = useState({});
    const [qtyEdits, setQtyEdits] = useState({});

    const [selectedClient, setSelectedClient] = useState('');
    const [selectedSubClient, setSelectedSubClient] = useState('');
    const [saleDate, setSaleDate] = useState(todayFormatted());
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (uid && roleId) dispatch(fetchCostCenters({ uid, rid: roleId }));
        dispatch(fetchCategories());
        return () => dispatch(resetScrapSale());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset per-row edits whenever the grid refreshes
    useEffect(() => {
        setCheckedRows({}); setBasicEdits({}); setQtyEdits({});
    }, [gridItems]);

    useEffect(() => {
        if (addResult) {
            toast.success('Items Added Successfully.');
            dispatch(clearAddResult());
            if (selectedCC) {
                dispatch(fetchGrid(selectedCC));
                dispatch(fetchSaleMinDate(selectedCC));
            }
        } else if (errors.add) {
            toast.error(errors.add);
            dispatch(clearAddResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addResult, errors.add]);

    useEffect(() => {
        if (submitResult) {
            toast.success(`Scrap Code  ${submitResult.code}  Generated Successfully`);
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Cost Center ──────────────────────────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setCategory(''); setItemCode('');
        setSelectedClient(''); setSelectedSubClient('');
        dispatch(clearItemCodes());
        dispatch(clearGrid());
        dispatch(clearClients());
        if (cc) {
            dispatch(fetchClients(cc));
            dispatch(fetchGrid(cc)).then((result) => {
                if (fetchGrid.fulfilled.match(result) && (result.payload?.length || 0) > 0) {
                    dispatch(fetchSaleMinDate(cc));
                }
            });
        }
    };

    // ── Item search / add ────────────────────────────────────────────────────

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setItemCode('');
        dispatch(clearItemCodes());
        if (e.target.value && selectedCC) {
            dispatch(fetchItemCodes({ cat: e.target.value, cc: selectedCC }));
        }
    };

    const handleClearSearch = () => {
        setCategory(''); setItemCode('');
        dispatch(clearItemCodes());
    };

    const handleAddItem = () => {
        if (!selectedCC) { toast.error('Select Cost Center'); return; }
        if (!category)   { toast.error('Select Item Category'); return; }
        if (!itemCode)   { toast.error('Select Item Code'); return; }

        dispatch(addItem({ ScrapListId: itemCode, Costcenter: selectedCC }));
    };

    // ── Grid row edits ───────────────────────────────────────────────────────

    const toggleRowChecked = (id) => setCheckedRows((prev) => ({ ...prev, [id]: !prev[id] }));

    const handleQtyChange = (row, value) => {
        const avlQty = num(row.AvailableQty);
        if (value !== '' && num(value) > avlQty) {
            toast.error('Invalid Quantity');
            setQtyEdits((prev) => ({ ...prev, [row.ScrapListId]: '' }));
            return;
        }
        setQtyEdits((prev) => ({ ...prev, [row.ScrapListId]: value }));
    };

    const sellingAmountFor = (row) => {
        const basic = basicEdits[row.ScrapListId];
        const qty = qtyEdits[row.ScrapListId];
        if (!basic || !qty) return 0;
        return num(basic) * num(qty);
    };

    const handleDeleteRow = (row) => {
        if (!checkedRows[row.ScrapListId]) { toast.error('Please Check for Delete'); return; }
        dispatch(deleteItem({ ScrapListId: row.ScrapListId })).then((result) => {
            if (deleteItem.fulfilled.match(result)) {
                toast.success('Deleted Successfully');
                if (selectedCC) dispatch(fetchGrid(selectedCC));
            } else {
                toast.error(result.payload || 'Failed to delete item');
            }
        });
    };

    const subtotal = gridItems.reduce((s, r) => s + sellingAmountFor(r), 0);

    // ── Client / Sub-client ──────────────────────────────────────────────────

    const handleClientChange = (e) => {
        const val = e.target.value;
        setSelectedClient(val);
        setSelectedSubClient('');
        dispatch(clearSubClients());
        if (val && selectedCC) dispatch(fetchSubClients({ cc: selectedCC, clientcode: val }));
    };

    // ── Submit ───────────────────────────────────────────────────────────────

    const handleSubmit = () => {
        if (!selectedCC)        { toast.error('Select Cost Center'); return; }
        if (!selectedClient)    { toast.error('Select Client Name'); return; }
        if (!selectedSubClient) { toast.error('Select Sub Client Name'); return; }
        if (!saleDate)          { toast.error('Select Date'); return; }
        if (!remarks.trim())    { toast.error('Enter Remarks'); return; }
        if (gridItems.length === 0) { toast.error('Please Add Items to Scrap Sale'); return; }
        if (!gridItems.every((r) => checkedRows[r.ScrapListId])) { toast.error('Please Verify Scrap Item Codes'); return; }
        if (!gridItems.every((r) => num(qtyEdits[r.ScrapListId]) > 0)) { toast.error('Enter Selling Quantity'); return; }
        if (!gridItems.every((r) => basicEdits[r.ScrapListId] !== undefined && basicEdits[r.ScrapListId] !== '')) {
            toast.error('Enter Selling Basic Price');
            return;
        }

        let rowids = '', basics = '', qtys = '', amounts = '';
        let total = 0;
        gridItems.forEach((r) => {
            const amount = sellingAmountFor(r);
            rowids  += `${r.ScrapListId},`;
            basics  += `${num(basicEdits[r.ScrapListId])},`;
            qtys    += `${num(qtyEdits[r.ScrapListId])},`;
            amounts += `${amount},`;
            total += amount;
        });

        const subClientRow = subClientList.find((s) => String(s.SubClientId) === String(selectedSubClient));
        const subClientCode = subClientCodeOf(subClientRow?.SubClientCode);

        dispatch(submitScrapSale({
            ScrapListIds: rowids,
            Basics: basics,
            Quantitys: qtys,
            Amounts: amounts,
            TotalAmount: total,
            Costcenter: selectedCC,
            SaleDate: formatDateForSP(saleDate),
            Remarks: remarks,
            ClientName: selectedClient,
            SubclientName: subClientCode,
            RoleID: String(roleId),
            Createdby: currentUser,
        }));
    };

    const handleReset = () => {
        setSelectedCC(''); setCategory(''); setItemCode('');
        setCheckedRows({}); setBasicEdits({}); setQtyEdits({});
        setSelectedClient(''); setSelectedSubClient('');
        setSaleDate(todayFormatted());
        setRemarks('');
        dispatch(clearItemCodes());
        dispatch(clearGrid());
        dispatch(clearClients());
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = gridItems.length > 0;
    const checkedCount = gridItems.filter((r) => checkedRows[r.ScrapListId]).length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow">
                        <Recycle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Scrap Sale</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Raise a scrap sale for stock items at a cost center.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Cost Center selection ─────────────────────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>CC/Job Code</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)} disabled={loading.ccList}>
                                <option value="">-Please Select-</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>
                    {loading.grid && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Loading items…</div>
                    )}
                </div>
            </SectionCard>

            {/* ── Item search ──────────────────────────────────────────────── */}
            {selectedCC && (
                <SectionCard icon={Search} title="Search &amp; Add Item" accent="amber">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="min-w-[220px]">
                            <Label required>Item Category</Label>
                            <select className={inputCls} value={category} onChange={handleCategoryChange} disabled={loading.categoryList}>
                                <option value="">-Please Select-</option>
                                {categoryList.map((c) => (
                                    <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1 min-w-[240px]">
                            <Label required>Item Codes</Label>
                            <div className="relative">
                                <select className={inputCls} value={itemCode} onChange={(e) => setItemCode(e.target.value)}
                                    disabled={!category || loading.itemCodeList}>
                                    <option value="">Select</option>
                                    {itemCodeList.map((i) => (
                                        <option key={i.Itemid} value={i.Itemid}>{i.ItemName}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    {loading.itemCodeList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={handleAddItem} disabled={loading.add}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.add ? <SpinnerIcon /> : <Plus className="w-4 h-4" />} Add
                            </button>
                            <button onClick={handleClearSearch}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── Staged items grid ────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Layers} title="Scrap Items"
                    subtitle={`${checkedCount} of ${gridItems.length} item${gridItems.length !== 1 ? 's' : ''} verified`}
                    accent="indigo">
                    <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                    {['Chk', 'Item Code', 'Item Name', 'Specification', 'DCA', 'Sub DCA', 'Company Basic', 'Units', 'Avl Qty', 'Selling Basic', 'Selling Qty', 'Selling Amount', ''].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {gridItems.map((row, idx) => (
                                    <tr key={row.ScrapListId} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                        <td className="px-2.5 py-2">
                                            <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                checked={!!checkedRows[row.ScrapListId]} onChange={() => toggleRowChecked(row.ScrapListId)} />
                                        </td>
                                        <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{row.ItemCode}</td>
                                        <td className="px-2.5 py-2 text-gray-900 dark:text-white">{row.ItemName}</td>
                                        <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{row.Specification}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.DcaCode}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.SubDcaCode}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300">{row.BasicPrice}</td>
                                        <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{row.Units}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{row.AvailableQty}</td>
                                        <td className="px-1.5 py-1.5 w-24">
                                            <input type="number" min="0" step="0.01"
                                                className={`${inputCls} text-right`}
                                                value={basicEdits[row.ScrapListId] ?? ''}
                                                onChange={(e) => setBasicEdits((p) => ({ ...p, [row.ScrapListId]: e.target.value }))} />
                                        </td>
                                        <td className="px-1.5 py-1.5 w-24">
                                            <input type="number" min="0" step="0.001"
                                                className={`${inputCls} text-right`}
                                                value={qtyEdits[row.ScrapListId] ?? ''}
                                                onChange={(e) => handleQtyChange(row, e.target.value)} />
                                        </td>
                                        <td className="px-2.5 py-2 text-right font-semibold text-indigo-700 dark:text-indigo-400">
                                            {sellingAmountFor(row).toFixed(2)}
                                        </td>
                                        <td className="px-1.5 py-1.5">
                                            <button onClick={() => handleDeleteRow(row)} disabled={loading.deleteItem}
                                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 disabled:opacity-50">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t-2 border-indigo-200 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/20 font-semibold text-gray-700 dark:text-gray-200">
                                    <td colSpan={11} className="px-2.5 py-2 text-right">SubTotal</td>
                                    <td className="px-2.5 py-2 text-right">{subtotal.toFixed(2)}</td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label required>Client Name</Label>
                            <select className={inputCls} value={selectedClient} onChange={handleClientChange} disabled={loading.clients}>
                                <option value="">-Please Select-</option>
                                {clientList.map((c) => (
                                    <option key={c.ClientID} value={c.ClientCode}>{c.ClientName}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label required>Sub Client Name</Label>
                            <select className={inputCls} value={selectedSubClient} onChange={(e) => setSelectedSubClient(e.target.value)}
                                disabled={!selectedClient || loading.subClients}>
                                <option value="">-Please Select-</option>
                                {subClientList.map((sc) => (
                                    <option key={sc.SubClientId} value={sc.SubClientId}>{sc.SubClientCode}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label required>Date</Label>
                            <CustomDatePicker value={saleDate} onChange={(d) => setSaleDate(formatDateForSP(d))}
                                format="DD-MMM-YYYY" placeholder="Select Date"
                                minDate={saleMinDate || undefined} maxDate={new Date()} />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default ScrapSale;
